import React, {Dispatch, FC, SetStateAction, useEffect, useRef} from 'react';
import {Col, Form, Input, message} from "antd";
import {CustomFieldInput} from "../../../../../components/UI/AntCustom/CustomFieldInput";
import {CustomButton} from "../../../../../components/UI/AntCustom/CustomButton";
import {CustomModal} from "../../../../../components/UI/AntCustom/CustomModal";
import {renderSuccessModal} from "../../../../../components/UI/StatusModals/renderSuccessModal";
import {defineMessages, useIntl} from "react-intl";
import {CommonFormMessages} from "../../../../../intl/commonMessages/CommonFormMessages";
import {CommonButtonMessages} from "../../../../../intl/commonMessages/CommonButtonMessages";
import {useAppDispatch, useAppSelector} from "../../../../../store/store";
import {
  clearCreateMessageTemplate,
  clearUpdateMessageTemplate,
  createMessageTemplate,
  updateMessageTemplate
} from "../../../../../store/reducers/messageTemplatesReducer/messageTemplatesReducer";

const CreateMessageTemplateModalMessages = defineMessages({
  createTitle: {
    id: "createMessageTemplateModal.createTitle",
    defaultMessage: "Create Message Template",
  },
  editTitle: {
    id: "createMessageTemplateModal.editTitle",
    defaultMessage: "Edit Message Template",
  },
  nameLabel: {
    id: "createMessageTemplateModal.nameLabel",
    defaultMessage: "Template Name",
  },
  namePlaceholder: {
    id: "createMessageTemplateModal.namePlaceholder",
    defaultMessage: "Enter template name",
  },
  textLabel: {
    id: "createMessageTemplateModal.textLabel",
    defaultMessage: "Template Text",
  },
  textPlaceholder: {
    id: "createMessageTemplateModal.textPlaceholder",
    defaultMessage: "Enter template text",
  },
  createSuccess: {
    id: "createMessageTemplateModal.createSuccess",
    defaultMessage: "New Message Template has been successfully created",
  },
  editSuccess: {
    id: "createMessageTemplateModal.editSuccess",
    defaultMessage: "Message Template has been successfully updated",
  }
})

interface ICreateMessageTemplateFormData {
  name: string;
  text: string;
}

interface IMessageTemplateData {
  id?: number;
  name: string;
  text: string;
}

interface ICreateMessageTemplateModal {
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  templateData?: IMessageTemplateData;
  onSuccess?: () => void;
}

export const CreateMessageTemplateModal: FC<ICreateMessageTemplateModal> = ({isModalVisible, setIsModalVisible, templateData, onSuccess}) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isEditMode = !!templateData;
  const successHandledRef = useRef(false);
  
  const {
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
    errorMessage: createErrorMessage,
  } = useAppSelector(state => state.messageTemplates.create);
  
  const {
    isLoading: isUpdateLoading,
    isSuccess: isUpdateSuccess,
    errorMessage: updateErrorMessage,
  } = useAppSelector(state => state.messageTemplates.update);

  const isLoading = isEditMode ? isUpdateLoading : isCreateLoading;
  const isSuccess = isEditMode ? isUpdateSuccess : isCreateSuccess;
  const errorMessage = isEditMode ? updateErrorMessage : createErrorMessage;

  useEffect(() => {
    return () => {
      dispatch(clearCreateMessageTemplate());
      dispatch(clearUpdateMessageTemplate());
    }
  }, [])

  useEffect(() => {
    // Сбрасываем флаг при изменении режима редактирования
    successHandledRef.current = false;
  }, [isEditMode])

  useEffect(() => {
    if (isModalVisible) {
      successHandledRef.current = false;
      if (templateData) {
        form.setFieldsValue({
          name: templateData.name,
          text: templateData.text,
        });
      } else {
        form.resetFields();
      }
    } else {
      // Сброс состояния при закрытии модалки
      successHandledRef.current = false;
      dispatch(clearCreateMessageTemplate());
      dispatch(clearUpdateMessageTemplate());
    }
  }, [isModalVisible, templateData, dispatch])

  // Обработка успеха создания
  useEffect(() => {
    if (isModalVisible && !isCreateLoading && isCreateSuccess && !isEditMode && !successHandledRef.current) {
      successHandledRef.current = true;
      const successMessage = intl.formatMessage(CreateMessageTemplateModalMessages.createSuccess);
      
      setIsModalVisible(false);
      form.resetFields();
      dispatch(clearCreateMessageTemplate());
      
      renderSuccessModal(successMessage, () => {
        if (onSuccess) {
          onSuccess();
        }
      });
    }
    
    if (isModalVisible && !isCreateLoading && createErrorMessage && !isEditMode) {
      message.error(createErrorMessage);
    }
  }, [isCreateSuccess, createErrorMessage, isCreateLoading, isModalVisible, isEditMode, dispatch, intl, setIsModalVisible, form, onSuccess])

  // Обработка успеха обновления
  useEffect(() => {
    if (isModalVisible && !isUpdateLoading && isUpdateSuccess && isEditMode && !successHandledRef.current) {
      successHandledRef.current = true;
      const successMessage = intl.formatMessage(CreateMessageTemplateModalMessages.editSuccess);
      
      setIsModalVisible(false);
      form.resetFields();
      dispatch(clearUpdateMessageTemplate());
      
      renderSuccessModal(successMessage, () => {
        if (onSuccess) {
          onSuccess();
        }
      });
    }
    
    if (isModalVisible && !isUpdateLoading && updateErrorMessage && isEditMode) {
      message.error(updateErrorMessage);
    }
  }, [isUpdateSuccess, updateErrorMessage, isUpdateLoading, isModalVisible, isEditMode, dispatch, intl, setIsModalVisible, form, onSuccess])

  const onFinish = (values: ICreateMessageTemplateFormData) => {
    if (isEditMode && templateData?.id) {
      dispatch(updateMessageTemplate({
        messageTemplateId: templateData.id,
        name: values.name,
        text: values.text,
      }));
    } else {
      dispatch(createMessageTemplate({
        name: values.name,
        text: values.text,
      }));
    }
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  return (
    <CustomModal
      title={intl.formatMessage(isEditMode ? CreateMessageTemplateModalMessages.editTitle : CreateMessageTemplateModalMessages.createTitle)}
      open={isModalVisible}
      onCancel={() => setIsModalVisible(false)}
      centered
      footer={null}
    >
      <Form
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        layout={'vertical'}
      >
        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CreateMessageTemplateModalMessages.nameLabel)}
            name={'name'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <Input 
              placeholder={intl.formatMessage(CreateMessageTemplateModalMessages.namePlaceholder)}
            />
          </CustomFieldInput>
        </Col>

        <Col flex={1}>
          <CustomFieldInput
            label={intl.formatMessage(CreateMessageTemplateModalMessages.textLabel)}
            name={'text'}
            rules={[{required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)}]}
          >
            <Input.TextArea 
              placeholder={intl.formatMessage(CreateMessageTemplateModalMessages.textPlaceholder)}
              autoSize={{minRows: 4, maxRows: 8}}
            />
          </CustomFieldInput>
        </Col>

        <Form.Item>
          <CustomButton
            block
            heightSize={"l"}
            type={'primary'}
            htmlType={'submit'}
            loading={isLoading}
          >
            {isEditMode ? intl.formatMessage(CommonButtonMessages.save) : intl.formatMessage(CommonButtonMessages.create)}
          </CustomButton>
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

